import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import { sessionStore } from "../../lib/store";
import { gaPushEvent } from "../../tools/analytics/base.analytics";
import { goBackInApp } from "../../domains/app/utils/backNavigation";
import { Button } from "../ui/Button";
import ContentHeader from "../app/headers/ContentHeader";
import ErrorBlock from "./ErrorBlock";

/**
 * One-shot flag to prevent infinite reload loops when a dynamic-import chunk
 * fails to load. Set on first error, cleared once reload succeeds.
 */
const chunkReloadAttemptedStore = sessionStore(
  "chunk-reload",
  false,
  z.boolean()
);

function getErrorMessage(error: unknown) {
  if (!error) return "Unknown error";
  if (error instanceof Error) {
    return error.message;
  }
  // `String(error)` flattens every thrown object to "[object Object]", which is
  // what 40% of this event's volume used to look like. Name the constructor and
  // its keys instead — enough to find the throw site without a stack.
  if (typeof error === "object") {
    const keys = Object.keys(error).sort().join(",");
    // A null-prototype object has no `constructor` — reading `.name` off it
    // would crash the crash handler.
    const ctor =
      Object.getPrototypeOf(error) === null
        ? "NullPrototype"
        : error.constructor.name;
    return `Non-Error thrown: ${ctor} {${keys}}`;
  }
  return `Non-Error thrown: ${typeof error} ${String(error)}`;
}

type ErrorKind = "chunk_load" | "storage_quota" | "thrown_non_error" | "render";

function getErrorKind(error: unknown, message: string): ErrorKind {
  if (
    message.includes("dynamically imported module") ||
    (message.includes("Failed to fetch") && message.includes(".js"))
  ) {
    return "chunk_load";
  }
  if (message.includes("quota has been exceeded")) return "storage_quota";
  if (!(error instanceof Error)) return "thrown_non_error";
  return "render";
}

/**
 * A groupable version of the error message. Without this, one bug produces one
 * row per occurrence: React embeds a per-instance fiber key in the message
 * ("__reactFiber$f4hosv8wiwd"), so the single most frequent crash we have was
 * split across ~14 distinct values and never surfaced in any report.
 */
function getErrorFingerprint(message: string): string {
  return message
    .replace(/__reactFiber\$[a-z0-9]+/gi, "__reactFiber$<key>")
    .replace(/__reactProps\$[a-z0-9]+/gi, "__reactProps$<key>")
    .replace(
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
      "<uuid>"
    )
    .replace(/\b[0-9a-f]{24}\b/gi, "<objectid>")
    .replace(/\b\d+\b/g, "<n>")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}

/**
 * The route pattern, not the URL: raw pathnames carry content IDs, so grouping
 * by them yields one row per article rather than one row per broken page.
 */
function getRoutePattern(pathname: string): string {
  return pathname
    .split("/")
    .map((segment) => {
      if (segment === "") return segment;
      if (/^[0-9a-f]{24}$/i.test(segment)) return ":id";
      if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment
        )
      ) {
        return ":id";
      }
      if (/^\d+$/.test(segment)) return ":n";
      return segment;
    })
    .join("/");
}

export default function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: unknown;
  resetErrorBoundary: () => void;
}) {
  const history = useHistory();
  const { t } = useTranslation();
  const message = import.meta.env.DEV
    ? getErrorMessage(error)
    : t("Unable to load content.");

  useEffect(() => {
    const msg = getErrorMessage(error);
    const kind = getErrorKind(error, msg);
    const isFirstChunkAttempt =
      kind === "chunk_load" && !chunkReloadAttemptedStore.get();

    // Track BEFORE the reload: this used to return early, so every chunk
    // failure that recovered on the retry was never counted at all.
    gaPushEvent("error_encountered", {
      error_location: "error_boundary",
      error_kind: kind,
      error_name: msg,
      error_fingerprint: getErrorFingerprint(msg),
      route: getRoutePattern(globalThis.location.pathname),
      is_chunk_reload_retry: kind === "chunk_load" && !isFirstChunkAttempt,
    });

    if (kind === "chunk_load") {
      if (isFirstChunkAttempt) {
        chunkReloadAttemptedStore.set(true);
        window.location.reload();
        return;
      }
      chunkReloadAttemptedStore.set(false);
    }
  }, [error]);

  // The boundary covers every page, public ones included: `/` is home for a
  // signed-in reader and the front door for a signed-out one.
  const handleBack = () => {
    goBackInApp(history, "/");
    resetErrorBoundary();
  };

  return (
    // The boundary fallback renders OUTSIDE the normal page layout, so it must
    // apply the safe-area insets itself — otherwise the header tucks under the
    // notch/status bar and the action can fall under the home indicator on iOS.
    <div className="min-h-screen flex flex-col pt-(--safe-area-top) pb-(--safe-area-bottom)">
      <ContentHeader onBack={handleBack} />
      <ErrorBlock
        title={t("Something went wrong")}
        message={message}
        action={
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={resetErrorBoundary}
          >
            {t("Try again")}
          </Button>
        }
      />
    </div>
  );
}
